/** Tab 6: Statutory */
import { useState } from 'react'
import {
  Alert,
  Button, Card, Form, Input, message, Space, DatePicker, Switch, InputNumber, Row, Col,
} from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'

interface Props { detail: JoiningDetail }

// PAN becomes mandatory only when a MONTHLY worker's annualised salary crosses
// the plant's TDS threshold (Section 192). Default ₹2,50,000/yr — mirrors the
// backend default in activation_service; the backend hard-blocks activation, so
// this is a guidance hint (a plant with a customised threshold stays authoritative
// server-side).
const PAN_TDS_ANNUAL_DEFAULT = 250000
const PAN_RE = /^[A-Z]{5}\d{4}[A-Z]$/

export default function StatutoryTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)

  const emp = detail.employee as Record<string, unknown>
  const monthlyBasic = Number(emp.basic_salary ?? 0)
  const annualSalary = emp.salary_type === 'MONTHLY' ? monthlyBasic * 12 : 0
  const panRequired = emp.salary_type === 'MONTHLY' && annualSalary >= PAN_TDS_ANNUAL_DEFAULT
  const currentPan = String(emp.pan_no ?? '')
  const panMissing = panRequired && !PAN_RE.test(currentPan.toUpperCase())

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const initial: Record<string, unknown> = { ...detail.employee }
  const dateFields = ['pf_join_date', 'esi_join_date']
  dateFields.forEach((f) => {
    if (initial[f]) initial[f] = dayjs(initial[f] as string)
  })

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" title="Provident Fund (PF)" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="pf_applicable" label="PF Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="uan_no" label="UAN No">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="new_uan_required" label="New UAN Required" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="pf_no" label="PF No">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="pf_join_date" label="PF Join Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="previous_pf_number" label="Previous PF Number">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="previous_uan" label="Previous UAN">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="eps_applicable" label="EPS Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="voluntary_pf_applicable" label="VPF Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="voluntary_pf_percent" label="VPF %">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="voluntary_pf_amount" label="VPF Amount">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="pf_wage_ceiling_applicable" label="Wage Ceiling Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="form_11_collected" label="Form 11 Collected" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title="ESI" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="esi_applicable" label="ESI Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="esi_no" label="ESI No">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="new_esic_required" label="New ESIC Required" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="esi_dispensary" label="ESI Dispensary">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="esi_join_date" label="ESI Join Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="esi_family_details_available" label="Family Details Available" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title="Other Statutory" style={{ marginBottom: 12 }}>
        {panRequired && panMissing && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="PAN required for TDS"
            description={
              `This worker's annual salary (~₹${annualSalary.toLocaleString('en-IN')}) `
              + `crosses the ₹${PAN_TDS_ANNUAL_DEFAULT.toLocaleString('en-IN')} TDS threshold, `
              + 'so a valid PAN is mandatory before activation. Without it, TDS must be '
              + 'deducted at the higher 20% rate (Section 206AA).'
            }
          />
        )}
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="pan_no"
              label={(
                <span>
                  PAN No
                  {panRequired && <span style={{ color: '#ff4d4f' }}> *</span>}
                </span>
              )}
              help={panRequired ? 'Format: ABCDE1234F — required for TDS at this salary' : undefined}
              normalize={(v: string) => (typeof v === 'string' ? v.replace(/\s/g, '').toUpperCase() : v)}
              rules={[{
                validator: (_r, value: string) => {
                  if (!value) {
                    return panRequired
                      ? Promise.reject(new Error('PAN required — salary crosses the TDS threshold'))
                      : Promise.resolve()
                  }
                  return PAN_RE.test(value.trim().toUpperCase())
                    ? Promise.resolve()
                    : Promise.reject(new Error('Invalid PAN — format ABCDE1234F'))
                },
              }]}
            >
              <Input placeholder="ABCDE1234F" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="lwf_applicable" label="LWF Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="pt_applicable" label="PT Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
